"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, PhoneCall, Clock, ShieldCheck, Navigation, CircleCheck as CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const neighborhoods = [{
  name: "Downtown Jonesboro",
  zip: "30236",
  status: "Active Dispatch"
}, {
  name: "Lake Spivey Area",
  zip: "30236",
  status: "Active Dispatch"
}, {
  name: "Tara Boulevard Corridor",
  zip: "30238",
  status: "Active Dispatch"
}, {
  name: "Mundy's Mill",
  zip: "30238",
  status: "Active Dispatch"
}, {
  name: "Irondale / North Jonesboro",
  zip: "30237",
  status: "Active Dispatch"
}, {
  name: "Lovejoy & South Clayton",
  zip: "30250",
  status: "Active Dispatch"
}, {
  name: "Flint River District",
  zip: "30236",
  status: "Active Dispatch"
}, {
  name: "Riverdale Border Zone",
  zip: "30274",
  status: "Active Dispatch"
}];
const highlights = [{
  text: "Local technicians stationed in Jonesboro, GA"
}, {
  text: "Average rapid arrival time of 30–45 minutes"
}, {
  text: "No travel surcharges within Clayton County"
}, {
  text: "Fully stocked service vans ready 24/7"
}];
export default function ServiceArea() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="jonesboro-service-area" data-nav="light" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto max-w-[1240px] px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column: Local Presence Copy & Emergency Triggers */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          ease: "easeOut"
        }} className="lg:col-span-6 flex flex-col justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-bold uppercase tracking-wider mb-4">
                <Navigation className="size-3.5 text-primary" />
                <span>Local Emergency Coverage</span>
              </div>

              <h2 className="font-default text-3xl md:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground leading-tight mb-4">
                Fast Plumbing Help Across Jonesboro & Clayton County
              </h2>

              <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6">
                When water is flooding your floor or a sewer line backs up, every minute counts. 
                Our emergency plumbing crews live and operate right here in Jonesboro, GA. Because we 
                are already local, our technicians bypass heavy highway delays and arrive at your 
                doorstep quickly with fully equipped repair vans.
              </p>

              {/* Trust Metric Box */}
              <div className="rounded-xl border border-border bg-card p-4 md:p-5 mb-8">
                <div className="grid grid-cols-2 gap-4">
                  <div className="border-r border-border pr-3">
                    <div className="flex items-center gap-1.5 text-accent-foreground font-bold text-xl md:text-2xl mb-1">
                      <Clock className="size-5 text-accent" />
                      <span>30-45 Min</span>
                    </div>
                    <p className="text-xs text-muted-foreground font-medium">Average Emergency Response</p>
                  </div>
                  <div className="pl-1">
                    <div className="flex items-center gap-1.5 text-foreground font-bold text-xl md:text-2xl mb-1">
                      <ShieldCheck className="size-5 text-primary" />
                      <span>24/7 Available</span>
                    </div>
                    <p className="text-xs text-muted-foreground font-medium">Nights, Weekends & Holidays</p>
                  </div>
                </div>
              </div>

              {/* Value Bullet List */}
              <ul className="space-y-2.5 mb-8">
                {highlights.map((item, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2.5 text-sm md:text-base text-foreground font-medium">
                    <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                    <span>{item.text}</span>
                  </li>)}
              </ul>
            </div>

            {/* CTA Dual Action Cluster */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              <Button asChild size="lg" className="rounded-full bg-accent text-accent-foreground font-bold hover:bg-accent/90 shadow-md shadow-accent/20 px-6 py-6 text-base justify-center">
                <a href="tel:4708479076" className="flex items-center gap-2">
                  <PhoneCall className="size-5" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full border-border bg-card text-foreground font-semibold hover:bg-muted/80 px-6 py-6 text-base justify-center">
                <Link to="/contact/" className="flex items-center gap-2">
                  <span>Request Online Dispatch</span>
                  <ArrowRight className="size-4 text-primary" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Local Coverage Grid Card & Map Area Visual */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-6">
            <Card className="border border-border bg-card shadow-sm rounded-2xl overflow-hidden py-0">
              {/* Header Status Bar inside Card */}
              <div className="bg-primary px-6 py-4 text-primary-foreground flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-accent"></span>
                  </span>
                  <span className="font-bold text-sm tracking-wide">Ready for Dispatch: Jonesboro, GA</span>
                </div>
                <div className="text-xs bg-primary-foreground/10 px-2.5 py-1 rounded-full text-primary-foreground/90 font-mono">
                  Clayton County Zone
                </div>
              </div>

              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-default text-lg md:text-xl font-bold text-foreground">
                      Primary Neighborhoods Served
                    </h3>
                    <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                      Direct mobile units patrolling the following zip codes daily:
                    </p>
                  </div>
                  <MapPin className="size-6 text-primary shrink-0 ml-2" />
                </div>

                {/* Neighborhood Coverage Grid */}
                <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
                once: true
              }} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6">
                  {neighborhoods.map((area, i) => <motion.div key={area.name} data-index={i} variants={itemVariants} className="flex items-center justify-between p-3 rounded-xl border border-border/80 bg-background hover:border-primary/50 transition-[border-color,box-shadow] duration-200 shadow-2xs">
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-foreground leading-tight">
                          {area.name}
                        </span>
                        <span className="text-xs font-mono text-muted-foreground mt-0.5">
                          ZIP: {area.zip}
                        </span>
                      </div>
                      <span className="text-[11px] font-medium text-chart-4 bg-chart-4/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {area.status}
                      </span>
                    </motion.div>)}
                </motion.div>

                {/* Local Guarantee Card Footer */}
                <div className="rounded-xl bg-secondary/60 border border-border p-4 flex items-start gap-3">
                  <Sparkles className="size-5 text-primary shrink-0 mt-0.5" />
                  <div className="text-xs md:text-sm text-foreground/90">
                    <span className="font-bold text-foreground block mb-0.5">Don't see your specific street?</span>
                    We service all residential homes and commercial properties within a 20-mile radius of downtown Jonesboro.
                  </div>
                </div>

                <div className="mt-5 text-center">
                  <Button asChild variant="link" className="text-primary font-semibold text-sm hover:underline p-0 h-auto inline-flex items-center gap-1.5">
                    <Link to="/service-areas/">
                      <span>View All Regional Georgia Service Areas</span>
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}